import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from '@mui/material';
import { BroadcastChannel } from 'broadcast-channel';
import { useIdleTimer } from 'react-idle-timer';
import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';
import log from 'loglevel';
import { useUserContext } from '../../context/userContext';

log.setLevel(log.levels.INFO);

// ===========================================
// Constants for Session Timing
// ===========================================
// 20-minute session timeout
const SESSION_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes in ms

// 2-minute warning before logout
const WARNING_DURATION_MS = 2 * 60 * 1000; // 2 minutes in ms
const WARNING_DURATION_SECONDS = WARNING_DURATION_MS / 1000;

// Time at which the warning dialog appears before session ends
// (Session ends at 20 minutes, warning at 2 minutes before => 18 minutes)
const WARNING_BEFORE_LOGOUT_MS = SESSION_TIMEOUT_MS - WARNING_DURATION_MS;

// ===========================================
// Types
// ===========================================
type SessionEvent = { event: 'resetTimer' | 'invalidate' };

export default function SessionHandler() {
  const { setUser, setIsAuthenticated } = useUserContext();

  const [showIdleWarningDialog, setShowIdleWarningDialog] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState<number>(
    WARNING_DURATION_SECONDS
  );

  // Reference for the countdown interval
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Broadcast channel for cross-tab communication
  const sessionEventChannel = useMemo(
    () => new BroadcastChannel<SessionEvent>('sessionEvent'),
    []
  );

  const handleUserLogout = useCallback(async () => {
    log.debug('Attempting to log out user due to inactivity...');
    try {
      const currentUser = await getCurrentUser();
      const { username, userId } = currentUser;
      log.debug(`Logging out user: ${username}, ID: ${userId}`);

      const initialUserSignIn = sessionStorage.getItem('initialUserSignIn');
      if (initialUserSignIn) {
        sessionStorage.removeItem('initialUserSignIn');
        log.debug('Removed initialUserSignIn from sessionStorage');
      }

      await signOut();
      setUser(null);
      setIsAuthenticated(false);
      log.debug('User successfully logged out due to inactivity.');
    } catch (error) {
      log.error('Error during logout, attempting fallback signOut:', error);
      // Fallback in case getCurrentUser or signOut fails initially
      try {
        await signOut();
      } catch (innerError) {
        log.error(
          'Error signing out after failure to get current user:',
          innerError
        );
      }
      setUser(null);
      setIsAuthenticated(false);
    }
  }, [setIsAuthenticated, setUser]);

  const handleWindowFocus = async () => {
    log.debug('Window focused, verifying session...');
    try {
      await fetchAuthSession();
      log.debug('Session validated upon window focus, still valid.');
    } catch (err) {
      log.error('Session expired/invalid upon focus. Reloading page...', err);
      window.location.reload();
    }
  };

  const handleSessionIdle = useCallback(async () => {
    log.debug(
      'Session has become idle, broadcasting "invalidate" event to all tabs.'
    );
    void sessionEventChannel.postMessage({ event: 'invalidate' });
    await handleUserLogout();
  }, [handleUserLogout, sessionEventChannel]);

  const handleSessionIdleWarning = useCallback(() => {
    log.debug('Warning threshold reached, showing idle warning dialog.');
    setCountdownSeconds(WARNING_DURATION_SECONDS);
    setShowIdleWarningDialog(true);
  }, []);

  const handleUserAction = useCallback(() => {
    log.trace(
      'User activity detected, resetting session timers across all tabs.'
    );
    void sessionEventChannel.postMessage({ event: 'resetTimer' });
  }, [sessionEventChannel]);

  const { reset: resetSessionTimer } = useIdleTimer({
    timeout: SESSION_TIMEOUT_MS,
    onIdle: handleSessionIdle,
    onAction: handleUserAction,
    debounce: 250,
  });

  const { reset: resetWarningTimer } = useIdleTimer({
    timeout: WARNING_BEFORE_LOGOUT_MS,
    onIdle: handleSessionIdleWarning,
  });

  const resetSessionTimers = useCallback(() => {
    log.debug('Resetting session timers (dialog closed or user interaction).');
    resetSessionTimer();
    resetWarningTimer();
    setShowIdleWarningDialog(false);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, [resetSessionTimer, resetWarningTimer]);

  // Add a global function for console-triggered testing
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__TEST_IDLE_DIALOG__ = () => {
      log.debug('Triggered idle warning dialog from console testing function.');
      setCountdownSeconds(WARNING_DURATION_SECONDS);
      setShowIdleWarningDialog(true);
    };
  }, []);

  // Add a key combo (Ctrl+Shift+W) to trigger the warning dialog for testing
  useEffect(() => {
    const handleKeydown = (e: KeyboardEvent) => {
      // Press Ctrl+Shift+W to show the warning dialog manually.
      if (e.ctrlKey && e.shiftKey && e.key === 'W') {
        log.debug(
          'Session timer key combo pressed, showing idle warning dialog.'
        );
        setCountdownSeconds(WARNING_DURATION_SECONDS);
        setShowIdleWarningDialog(true);
      }
    };
    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  useEffect(() => {
    window.addEventListener('focus', handleWindowFocus);
    log.debug('SessionHandler mounted, now listening for window focus events.');
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      log.debug(
        'SessionHandler unmounted, stopped listening for window focus events.'
      );
    };
  }, []);

  useEffect(() => {
    log.debug('Setting up SessionEvent channel message handler.');
    sessionEventChannel.onmessage = message => {
      log.debug(`Received message from sessionEventChannel: ${message.event}`);
      if (message.event === 'resetTimer') {
        resetSessionTimers();
      } else if (message.event === 'invalidate') {
        void handleUserLogout();
      }
    };

    return () => {
      log.debug('Cleaning up session event channel on component unmount.');
      void sessionEventChannel.close();
    };
  }, [handleUserLogout, resetSessionTimers, sessionEventChannel]);

  // Countdown effect for the warning dialog
  useEffect(() => {
    if (showIdleWarningDialog) {
      log.debug('Starting countdown for the idle warning dialog.');
      countdownIntervalRef.current = setInterval(() => {
        setCountdownSeconds(prev => {
          const newCount = prev - 1;
          if (newCount <= 0) {
            log.debug(
              'Warning countdown reached zero, invalidating session now.'
            );
            void handleSessionIdle();
            return 0;
          }
          return newCount;
        });
      }, 1000);
    } else if (!showIdleWarningDialog && countdownIntervalRef.current) {
      log.debug(
        'Idle warning dialog closed or reset, clearing countdown interval.'
      );
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [handleSessionIdle, showIdleWarningDialog]);

  return (
    <React.Fragment>
      {showIdleWarningDialog && (
        <Dialog
          open={true}
          onClose={resetSessionTimers}
          onMouseMove={resetSessionTimers}
        >
          <DialogContent>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
            >
              <Typography variant="h5" gutterBottom>
                Your session is about to expire
              </Typography>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={2}
                marginBottom={2}
              >
                <CircularProgress
                  variant="determinate"
                  // Calculate progress as percentage of total warning time
                  value={(countdownSeconds / WARNING_DURATION_SECONDS) * 100}
                  size={50}
                  thickness={5}
                />
                <Typography variant="body1">
                  Logging out in {countdownSeconds} seconds...
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Move your mouse, press a key, or interact with the page to
                remain logged in.
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </React.Fragment>
  );
}
